/**
 * Collapsible sidebar section. Wraps the header + child nav items so the
 * user can fold away Settings / Admin when they don't need them. Main /
 * Content / Growth stay flat (rendered by `SectionHeader` directly).
 *
 * Open state lives in localStorage under `merkflow-sidebar-groups` as a
 * `{ settings: boolean, admin: boolean }` object. First-load default is
 * closed for both. If the current URL starts with `routePrefix` the group
 * auto-opens on mount and that auto-open is persisted — so hitting
 * `/admin/plans` from anywhere reveals Admin even if the user had folded it.
 *
 * In whole-sidebar icon-only mode (`collapsed` prop) the header chevron
 * doesn't render; items stay hidden when the group is closed, matching the
 * expanded behaviour. The user re-expands the sidebar to interact.
 *
 * Animation: CSS grid row-trick (`grid-rows-[1fr|0fr]`) — no layout thrash
 * from measuring child height. 180ms ease-out per the brief.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';

const STORAGE_KEY = 'merkflow-sidebar-groups';

export type SidebarGroupKey = 'settings' | 'admin';

type GroupsState = Partial<Record<SidebarGroupKey, boolean>>;

function readInitial(storageKey: SidebarGroupKey, routePrefix: string): boolean {
  if (typeof window === 'undefined') return false;
  // Active-route override takes priority over persisted state — user expects
  // direct-URL navigation to always reveal the relevant group.
  if (window.location.pathname.startsWith(routePrefix)) return true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as GroupsState;
    return Boolean(parsed[storageKey]);
  } catch {
    return false;
  }
}

interface SidebarGroupProps {
  label: string;
  storageKey: SidebarGroupKey;
  routePrefix: string;
  collapsed: boolean;
  accent?: boolean;
  children: React.ReactNode;
}

export function SidebarGroup({
  label,
  storageKey,
  routePrefix,
  collapsed,
  accent = false,
  children,
}: SidebarGroupProps) {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState<boolean>(() => readInitial(storageKey, routePrefix));

  // Keep auto-open honouring client-side navigation too. If the user was on
  // /dashboard/posts, had Settings closed, then navigates to
  // /dashboard/settings/billing via a link, we want Settings to pop open.
  useEffect(() => {
    if (pathname.startsWith(routePrefix)) {
      setIsOpen((prev) => prev || true);
    }
    // Intentionally only watching pathname — storageKey/routePrefix are
    // stable for the lifetime of the component.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- routePrefix/storageKey are component-lifetime-stable; setIsOpen is a stable setter
  }, [pathname]);

  // Persist open state. Separate effect so auto-open + manual toggle both
  // flow through the same write path.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed: GroupsState = raw ? (JSON.parse(raw) as GroupsState) : {};
      parsed[storageKey] = isOpen;
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    } catch {
      // Storage can fail (private mode, quota). Swallow — a lost preference
      // is annoying but not worth surfacing to the user.
    }
  }, [isOpen, storageKey]);

  // Icon-only sidebar mode: no clickable header (no space for a chevron +
  // label). Closed groups keep their items hidden; the user re-expands the
  // whole sidebar to interact. Matches the brief's "hidden the same as
  // before" semantics.
  if (collapsed) {
    return (
      <div>
        <div className="mt-4 mb-1 h-2" />
        <GroupPanel isOpen={isOpen}>{children}</GroupPanel>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="group focus-visible:ring-accent/40 mt-4 mb-1 flex w-full items-center gap-2 px-5 py-1 transition-colors focus:outline-none focus-visible:ring-2"
      >
        <p
          className={`mk-section-label group-hover:!text-accent transition-colors ${
            accent ? '!text-accent' : ''
          }`}
        >
          {label}
        </p>
        <div className="bg-border h-px flex-1" />
        {isOpen ? (
          <ChevronDown className="text-text-tertiary group-hover:text-accent h-3.5 w-3.5 transition-colors" />
        ) : (
          <ChevronRight className="text-text-tertiary group-hover:text-accent h-3.5 w-3.5 transition-colors" />
        )}
      </button>
      <GroupPanel isOpen={isOpen}>{children}</GroupPanel>
    </div>
  );
}

/**
 * CSS-grid collapse. `grid-rows-[1fr]` → natural height, `grid-rows-[0fr]`
 * → zero, transitioned. Child lives inside `overflow-hidden` so the clip
 * animates. Opacity is transitioned on the same curve so the content
 * doesn't flash during the final pixels of the height animation.
 */
function GroupPanel({ isOpen, children }: { isOpen: boolean; children: React.ReactNode }) {
  return (
    <div
      role="group"
      className={`grid transition-all duration-[180ms] ease-out ${
        isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      }`}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}
